// TanStack Query hooks for the IP Case Sheet.
//
// ── The loading strategy, in one place ───────────────────────────
//
// First paint is TWO requests: useCaseSheet (header + snapshot + alerts, one
// server-side aggregated round-trip) and useTimeline. Everything else is a
// section hook with `enabled` gated on its tab being open, so opening a
// patient never pays for eleven sections nobody looked at.
//
// Every hook keys off `branchVersion` as well as branchKey()'s embedded branch
// id — see queryKeys.ts's invalidateBranchScoped docstring for the race that
// closes. An admission is branch-scoped, so a branch switch must evict this
// whole subtree rather than leave another site's ward round on screen.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import * as apiFns from './api';
import type {
  TimelineType, AssessmentInput, ProgressNoteInput, NursingNoteInput,
  MedicationOrderInput, AdministrationInput, InvestigationInput, ProcedureInput,
  ConsultInput, ConsultStatus, CarePlanInput, Vitals, FluidDirection,
  DispenseOrderInput, ParticularsInput, AttenderInput, MlcInput, PayerInput, ConsentInput,
  NursingAssessmentInput, RiskScreenInput, RiskScreenType,
  ClinicalOrderInput, DiagnosisInput, DischargeSummaryInput,
} from './types';

/**
 * Everything under one admission, dropped together. Any mutation invalidates
 * this prefix rather than enumerating which sections it could have affected —
 * recording a dose changes the MAR, the timeline, the snapshot and possibly
 * the alerts, and listing those by hand is how one gets forgotten.
 */
function invalidateAdmission(qc: QueryClient, admissionId: number) {
  return qc.invalidateQueries({ queryKey: keys.ip.caseSheet(admissionId) });
}

function useAdmissionKey(admissionId: number, section: string, params?: Record<string, unknown>) {
  const { branchVersion } = useAuth();
  return [...keys.ip.caseSheetSection(admissionId, section, params), branchVersion];
}

// ── First paint ──────────────────────────────────────────────────

export function useCaseSheet(admissionId: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.ip.caseSheetSection(admissionId ?? 0, 'case-sheet'), branchVersion],
    queryFn: () => apiFns.fetchCaseSheet(admissionId as number),
    enabled: admissionId !== null,
    // Alerts and vitals move on a ward. Two minutes is fresh enough for a
    // round without making the screen twitch while a doctor reads it.
    staleTime: 120_000,
  });
}

export function useTimeline(
  admissionId: number | null,
  opts: { types?: TimelineType[]; limit?: number; offset?: number; from?: string; author?: string } = {},
  enabled = true
) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'timeline', opts as Record<string, unknown>),
    queryFn: () => apiFns.fetchTimeline(admissionId as number, opts),
    enabled: admissionId !== null && enabled,
  });
}

// ── Sections — each gated on its tab being open ──────────────────

export function useAssessment(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'assessment'),
    queryFn: () => apiFns.fetchAssessment(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useProgressNotes(admissionId: number | null, includeSuperseded = false, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'progress-notes', { includeSuperseded }),
    queryFn: () => apiFns.fetchProgressNotes(admissionId as number, includeSuperseded),
    enabled: admissionId !== null && enabled,
  });
}

export function useNursingNotes(admissionId: number | null, shift?: string, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'nursing-notes', { shift: shift ?? null }),
    queryFn: () => apiFns.fetchNursingNotes(admissionId as number, shift),
    enabled: admissionId !== null && enabled,
  });
}

export function useVitals(admissionId: number | null, from?: string, enabled = true, includeSuperseded = false) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'vitals', { from: from ?? null, includeSuperseded }),
    queryFn: () => apiFns.fetchVitals(admissionId as number, from, undefined, includeSuperseded),
    enabled: admissionId !== null && enabled,
  });
}

/** Tenant-wide clinical policy (NEWS2 thresholds, the vitals entry-lag
 * policy). Changes rarely — five minutes is plenty fresh. */
export function useAlertRules(enabled = true) {
  return useQuery({
    queryKey: keys.ip.alertRules(),
    queryFn: apiFns.fetchAlertRules,
    enabled,
    staleTime: 300_000,
  });
}

export function useFluidBalance(admissionId: number | null, from?: string, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'fluid-balance', { from: from ?? null }),
    queryFn: () => apiFns.fetchFluidBalance(admissionId as number, from),
    enabled: admissionId !== null && enabled,
  });
}

export function useMedicationOrders(admissionId: number | null, status?: string, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'medication-orders', { status: status ?? null }),
    queryFn: () => apiFns.fetchMedicationOrders(admissionId as number, status),
    enabled: admissionId !== null && enabled,
  });
}

export function useMar(admissionId: number | null, date?: string, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'mar', { date: date ?? null }),
    queryFn: () => apiFns.fetchMar(admissionId as number, date),
    enabled: admissionId !== null && enabled,
    // The MAR is what a nurse works from during a drug round; it is the one
    // section where someone else's write in the last minute matters.
    staleTime: 30_000,
  });
}

export function useInvestigations(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'investigations'),
    queryFn: () => apiFns.fetchInvestigations(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useProcedures(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'procedures'),
    queryFn: () => apiFns.fetchProcedures(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useConsults(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'consults'),
    queryFn: () => apiFns.fetchConsults(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useCarePlan(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'care-plan'),
    queryFn: () => apiFns.fetchCarePlan(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useTransfers(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'transfers'),
    queryFn: () => apiFns.fetchTransfers(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useGapCheck(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'gap-check'),
    queryFn: () => apiFns.fetchGapCheck(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

export function useDischargeReadiness(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'discharge-readiness'),
    queryFn: () => apiFns.fetchDischargeReadiness(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}

/**
 * The AI gist is a MUTATION, not a query: it costs a model call, so it runs
 * when a clinician asks for it, never on mount and never on a refetch.
 */
export function useClinicalGist(admissionId: number) {
  return useMutation({
    mutationFn: () => apiFns.generateGist(admissionId),
  });
}

/**
 * Generates the Case Sheet .docx and files it in Patient Records. A pure
 * export of data already visible on screen — it changes no clinical fact, so
 * unlike every mutation below it does NOT invalidate the admission's cached
 * queries.
 */
export function useGenerateCaseSheetDocument(admissionId: number) {
  return useMutation({
    mutationFn: () => apiFns.generateCaseSheetDocument(admissionId),
  });
}

// ── Generate dialog (Phase 6/7) ───────────────────────────────────
// docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md §9.1.

/** The four seeded copy types — tenant-wide config, not admission-scoped, so it has no branch dimension and rarely changes; a long staleTime avoids re-fetching it every time the dialog opens. */
export function usePrintProfiles() {
  return useQuery({
    queryKey: ['ip', 'print-profiles'],
    queryFn: () => apiFns.fetchPrintProfiles(),
    staleTime: 10 * 60 * 1000,
  });
}

/** Powers the header's "MRD checklist" chip — deficiencies only, no render. */
export function useMrdChecklist(admissionId: number | null, profile?: string) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'mrd-checklist', { profile }),
    queryFn: () => apiFns.fetchMrdChecklist(admissionId as number, profile),
    enabled: admissionId !== null,
  });
}

/**
 * The PDF export — a pure read/release of already-visible data, same
 * non-invalidating posture as useGenerateCaseSheetDocument above. The
 * caller (GenerateCaseSheetDialog) reads `apiFns.blockedDeficiencies(error)`
 * on failure to tell "needs an override reason" apart from a real error.
 */
export function useGenerateCaseSheetExport(admissionId: number) {
  return useMutation({
    mutationFn: (input: import('./types').CaseSheetExportInput) => apiFns.generateCaseSheetExport(admissionId, input),
  });
}

// ── Mutations ────────────────────────────────────────────────────

function useAdmissionMutation<TArgs, TResult>(
  admissionId: number,
  fn: (args: TArgs) => Promise<TResult>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateAdmission(qc, admissionId),
  });
}

export const useSaveAssessment = (id: number) =>
  useAdmissionMutation(id, (v: { input: AssessmentInput; ifMatch?: string }) =>
    apiFns.saveAssessment(id, v.input, v.ifMatch));

export const useSignAssessment = (id: number) =>
  useAdmissionMutation(id, () => apiFns.signAssessment(id));

// ── Specialty add-ons (Surgical / OBG / Paediatric — Phase 8) ─────

/** The three template options — tenant-wide, not admission-scoped, rarely changes. */
export function useSpecialtyRecordTemplates() {
  return useQuery({
    queryKey: ['ip', 'specialty-record-templates'],
    queryFn: () => apiFns.fetchSpecialtyRecordTemplates(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSpecialtyRecords(admissionId: number | null) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'specialty-records'),
    queryFn: () => apiFns.fetchSpecialtyRecords(admissionId as number),
    enabled: admissionId !== null,
  });
}

export const useSaveSpecialtyRecord = (admissionId: number) =>
  useAdmissionMutation(admissionId, (v: { template: import('./types').SpecialtyTemplate; data: Record<string, unknown> }) =>
    apiFns.saveSpecialtyRecord(admissionId, v.template, v.data));

export const useSignSpecialtyRecord = (admissionId: number) =>
  useAdmissionMutation(admissionId, (id: number) => apiFns.signSpecialtyRecord(id));

export const useRecordReview = (id: number) =>
  useAdmissionMutation(id, (what: 'allergy' | 'medication') => apiFns.recordReview(id, what));

export const useCreateProgressNote = (id: number) =>
  useAdmissionMutation(id, (input: ProgressNoteInput) => apiFns.createProgressNote(id, input));

export const useUpdateProgressNote = (id: number) =>
  useAdmissionMutation(id, (v: { noteId: number; input: ProgressNoteInput; ifMatch?: string }) =>
    apiFns.updateProgressNote(v.noteId, v.input, v.ifMatch));

export const useSignProgressNote = (id: number) =>
  useAdmissionMutation(id, (noteId: number) => apiFns.signProgressNote(noteId));

export const useAmendProgressNote = (id: number) =>
  useAdmissionMutation(id, (v: { noteId: number; reason: string; changes: ProgressNoteInput }) =>
    apiFns.amendProgressNote(v.noteId, v.reason, v.changes));

export const useCreateNursingNote = (id: number) =>
  useAdmissionMutation(id, (input: NursingNoteInput) => apiFns.createNursingNote(id, input));

export const useRecordVitals = (id: number) =>
  useAdmissionMutation(id, (input: Partial<Vitals>) => apiFns.recordVitals(id, input));

export const useAmendVitals = (id: number) =>
  useAdmissionMutation(id, (v: { vitalsId: number; reason: string; changes: Partial<Vitals> }) =>
    apiFns.amendVitals(v.vitalsId, v.reason, v.changes));

export const useRecordFluid = (id: number) =>
  useAdmissionMutation(id, (input: {
    direction: FluidDirection; category: string; quantity_ml: number;
    route_detail?: string; remarks?: string; recorded_datetime?: string;
  }) => apiFns.recordFluid(id, input));

export const useDeleteFluidEntry = (id: number) =>
  useAdmissionMutation(id, (entryId: number) => apiFns.deleteFluidEntry(entryId));

export const useCreateMedicationOrder = (id: number) =>
  useAdmissionMutation(id, (input: MedicationOrderInput) => apiFns.createMedicationOrder(id, input));

export const useHoldOrder = (id: number) =>
  useAdmissionMutation(id, (v: { orderId: number; reason: string }) => apiFns.holdOrder(v.orderId, v.reason));

export const useResumeOrder = (id: number) =>
  useAdmissionMutation(id, (orderId: number) => apiFns.resumeOrder(orderId));

export const useDiscontinueOrder = (id: number) =>
  useAdmissionMutation(id, (v: { orderId: number; reason: string }) => apiFns.discontinueOrder(v.orderId, v.reason));

export const useDispenseOrder = (id: number) =>
  useAdmissionMutation(id, (v: { orderId: number; input: DispenseOrderInput }) => apiFns.dispenseOrder(v.orderId, v.input));

/**
 * Recording that a dose was given (or deliberately was not).
 *
 * Its own hook, distinct from every order mutation above, because it writes a
 * different table for a different clinical fact — the separation this module
 * exists to preserve.
 */
export const useRecordAdministration = (id: number) =>
  useAdmissionMutation(id, (v: { orderId: number; input: AdministrationInput }) =>
    apiFns.recordAdministration(v.orderId, v.input));

export const useCreateInvestigation = (id: number) =>
  useAdmissionMutation(id, (input: InvestigationInput) => apiFns.createInvestigation(id, input));

export const useUpdateInvestigation = (id: number) =>
  useAdmissionMutation(id, (v: { invId: number; input: Partial<InvestigationInput> }) =>
    apiFns.updateInvestigation(v.invId, v.input));

export const useReviewInvestigation = (id: number) =>
  useAdmissionMutation(id, (v: { invId: number; summary?: string }) =>
    apiFns.reviewInvestigation(v.invId, v.summary));

export const useCreateProcedure = (id: number) =>
  useAdmissionMutation(id, (input: ProcedureInput) => apiFns.createProcedure(id, input));

export const useCreateConsult = (id: number) =>
  useAdmissionMutation(id, (input: ConsultInput) => apiFns.createConsult(id, input));

export const useSetConsultStatus = (id: number) =>
  useAdmissionMutation(id, (v: { consultId: number; status: ConsultStatus; response_notes?: string; recommendations?: string }) =>
    apiFns.setConsultStatus(v.consultId, v.status, { response_notes: v.response_notes, recommendations: v.recommendations }));

export const useCreateCarePlanActivity = (id: number) =>
  useAdmissionMutation(id, (input: CarePlanInput) => apiFns.createCarePlanActivity(id, input));

export const useCompleteCarePlanActivity = (id: number) =>
  useAdmissionMutation(id, (v: { activityId: number; notes?: string }) =>
    apiFns.completeCarePlanActivity(v.activityId, v.notes));

export const useTransferBed = (id: number) =>
  useAdmissionMutation(id, (v: { bedId: number; reason?: string }) => apiFns.transferBed(id, v.bedId, v.reason));

// ── Face Sheet — admission particulars, attenders, MLC, payer ────
// Phase 1 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.1-6.4).

export function useParticulars(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'particulars'),
    queryFn: () => apiFns.fetchParticulars(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useUpdateParticulars = (id: number) =>
  useAdmissionMutation(id, (input: ParticularsInput) => apiFns.updateParticulars(id, input));

export function useAttenders(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'attenders'),
    queryFn: () => apiFns.fetchAttenders(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useCreateAttender = (id: number) =>
  useAdmissionMutation(id, (input: AttenderInput) => apiFns.createAttender(id, input));
export const useUpdateAttender = (id: number) =>
  useAdmissionMutation(id, (v: { attenderId: number; input: Partial<AttenderInput> }) =>
    apiFns.updateAttender(v.attenderId, v.input));
export const useDeleteAttender = (id: number) =>
  useAdmissionMutation(id, (attenderId: number) => apiFns.deleteAttender(attenderId));

export function useMlc(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'mlc'),
    queryFn: () => apiFns.fetchMlc(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useSaveMlc = (id: number) =>
  useAdmissionMutation(id, (input: MlcInput) => apiFns.saveMlc(id, input));

export function useLegalHold(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'legal-hold'),
    queryFn: () => apiFns.fetchLegalHold(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useSetLegalHold = (id: number) =>
  useAdmissionMutation(id, (reason: string) => apiFns.setLegalHold(id, reason));
export const useClearLegalHold = (id: number) =>
  useAdmissionMutation(id, (reason: string) => apiFns.clearLegalHold(id, reason));

/** Tenant-wide master list, not admission-scoped — a long cache is fine. */
export function usePayerSchemes(enabled = true) {
  return useQuery({
    queryKey: ['ip', 'payer-schemes'],
    queryFn: apiFns.fetchPayerSchemes,
    enabled,
    staleTime: 300_000,
  });
}
export function usePayers(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'payers'),
    queryFn: () => apiFns.fetchPayers(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useCreatePayer = (id: number) =>
  useAdmissionMutation(id, (input: PayerInput) => apiFns.createPayer(id, input));
export const useUpdatePayer = (id: number) =>
  useAdmissionMutation(id, (v: { payerId: number; input: Partial<PayerInput> }) =>
    apiFns.updatePayer(v.payerId, v.input));
export const useDeletePayer = (id: number) =>
  useAdmissionMutation(id, (payerId: number) => apiFns.deletePayer(payerId));
export const useVerifyPayerId = (id: number) =>
  useAdmissionMutation(id, (payerId: number) => apiFns.verifyPayerId(payerId));

/** Admin's payer master (insurer/TPA/scheme/corporate) — tenant-wide, long cache. */
export function usePayersMaster(q = '', enabled = true) {
  return useQuery({
    queryKey: ['payers-master', q],
    queryFn: () => apiFns.fetchPayersMaster(q),
    enabled,
    staleTime: 60_000,
  });
}

// ── Consent register ──────────────────────────────────────────────
// Phase 2 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.5).

export function useConsents(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'consents'),
    queryFn: () => apiFns.fetchConsents(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useCreateConsent = (id: number) =>
  useAdmissionMutation(id, (input: ConsentInput) => apiFns.createConsent(id, input));
export const useWithdrawConsent = (id: number) =>
  useAdmissionMutation(id, (v: { consentId: number; reason: string }) => apiFns.withdrawConsent(v.consentId, v.reason));

// ── Nursing assessment & risk screens ──────────────────────────────
// Phase 3 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.7).

export function useNursingAssessment(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'nursing-assessment'),
    queryFn: () => apiFns.fetchNursingAssessment(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useSaveNursingAssessment = (id: number) =>
  useAdmissionMutation(id, (input: NursingAssessmentInput) => apiFns.saveNursingAssessment(id, input));
export const useSignNursingAssessment = (id: number) =>
  useAdmissionMutation(id, () => apiFns.signNursingAssessment(id));

export function useRiskScreens(admissionId: number | null, screenType?: RiskScreenType, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'risk-screens', { screen_type: screenType ?? null }),
    queryFn: () => apiFns.fetchRiskScreens(admissionId as number, screenType),
    enabled: admissionId !== null && enabled,
  });
}
export const useCreateRiskScreen = (id: number) =>
  useAdmissionMutation(id, (input: RiskScreenInput) => apiFns.createRiskScreen(id, input));

// ── Clinical orders (Part B1) ────────────────────────────────────────
// Phase 4 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.8).

export function useClinicalOrders(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'clinical-orders'),
    queryFn: () => apiFns.fetchClinicalOrders(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useCreateClinicalOrder = (id: number) =>
  useAdmissionMutation(id, (input: ClinicalOrderInput) => apiFns.createClinicalOrder(id, input));
export const useCountersignClinicalOrder = (id: number) =>
  useAdmissionMutation(id, (orderId: number) => apiFns.countersignClinicalOrder(orderId));
export const useDiscontinueClinicalOrder = (id: number) =>
  useAdmissionMutation(id, (v: { orderId: number; reason: string }) => apiFns.discontinueClinicalOrder(v.orderId, v.reason));
export const useAmendClinicalOrder = (id: number) =>
  useAdmissionMutation(id, (v: { orderId: number; amendment_reason: string; changes: Partial<ClinicalOrderInput> }) =>
    apiFns.amendClinicalOrder(v.orderId, v.amendment_reason, v.changes));

// ── Admission diagnoses ──────────────────────────────────────────────
// Phase 4 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.9).

export function useDiagnoses(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'diagnoses'),
    queryFn: () => apiFns.fetchDiagnoses(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const useCreateDiagnosis = (id: number) =>
  useAdmissionMutation(id, (input: DiagnosisInput) => apiFns.createDiagnosis(id, input));
export const useSupersedeDiagnosis = (id: number) =>
  useAdmissionMutation(id, (v: { diagnosisId: number; changes: Partial<DiagnosisInput> }) =>
    apiFns.supersedeDiagnosis(v.diagnosisId, v.changes));

// ── Discharge summary ─────────────────────────────────────────────────
// Phase 5 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.11).

export function useDischargeSummary(admissionId: number | null, enabled = true) {
  return useQuery({
    queryKey: useAdmissionKey(admissionId ?? 0, 'discharge-summary'),
    queryFn: () => apiFns.fetchDischargeSummary(admissionId as number),
    enabled: admissionId !== null && enabled,
  });
}
export const usePutDischargeSummary = (id: number) =>
  useAdmissionMutation(id, (input: DischargeSummaryInput) => apiFns.putDischargeSummary(id, input));
export const useSignDischargeSummary = (id: number) =>
  useAdmissionMutation(id, (summaryId: number) => apiFns.signDischargeSummary(summaryId));
export const useAmendDischargeSummary = (id: number) =>
  useAdmissionMutation(id, (v: { summaryId: number; amendment_reason: string; changes: DischargeSummaryInput }) =>
    apiFns.amendDischargeSummary(v.summaryId, v.amendment_reason, v.changes));
